import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const AddCause = () => {
  const [formData, setFormData] = useState({
    cause: '',
    donationFor: '',
    description: '',
    name: '',
    number: '',
    address: '',
    isPermanent: 'true',
    visibleDays: ''
  });

  const [imageInputs, setImageInputs] = useState([{ id: 0, file: null }]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageInputChange = (e, index) => {
    const file = e.target.files[0];
    const updatedInputs = [...imageInputs];
    updatedInputs[index].file = file;
    setImageInputs(updatedInputs);

    const newPreviews = [...imagePreviews];
    newPreviews[index] = URL.createObjectURL(file);
    setImagePreviews(newPreviews);
  };

  const addImageInput = () => {
    if (imageInputs.length >= 5) {
      setUploadStatus('❌ Maximum 5 images allowed');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }
    setImageInputs([...imageInputs, { id: Date.now(), file: null }]);
  };

  const removeImageInput = (index) => {
    const updatedInputs = imageInputs.filter((_, i) => i !== index);
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
    setImageInputs(updatedInputs);
    setImagePreviews(updatedPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadStatus('');

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));
    imageInputs.forEach(({ file }) => {
      if (file) data.append('images', file);
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_REQUESTING_API}/api/causes`,
        data
      );
      setUploadStatus('✅ Cause added successfully!');
      
      // Reset form
      setFormData({
        cause: '',
        donationFor: '',
        description: '',
        name: '',
        number: '',
        address: '',
        isPermanent: 'true',
        visibleDays: ''
      });
      setImageInputs([{ id: 0, file: null }]);
      setImagePreviews([]);

      setTimeout(() => setUploadStatus(''), 5000);
    } catch (error) {
      console.error(error);
      setUploadStatus('❌ Failed to add cause. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-3">
            Add New Cause
          </h1>
          <p className="text-gray-600 text-lg">
            Create a new donation campaign
          </p>
        </motion.div>

        {/* Upload Status Message */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl font-semibold text-center ${
              uploadStatus.includes('✅')
                ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-red-100 text-red-800 border-2 border-red-300'
            }`}
          >
            {uploadStatus}
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200"
          >
            <div className="space-y-6">
              {/* Main Cause */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Main Cause *
                </label>
                <select
                  name="cause"
                  value={formData.cause}
                  onChange={handleChange}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  required
                >
                  <option value="">-- Select Main Cause --</option>
                  <option value="Masjid Construction">Masjid Construction</option>
                  <option value="Madrasa">Madrasa</option>
                  <option value="Imaam & Mauzin Funds">Imaam & Mauzin Funds</option>
                  <option value="Maktab & syllabaus">Maktab & syllabaus</option>
                  <option value="Social work">Social work</option>
                </select>
              </div>

              {/* Donation For */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Donation For *
                </label>
                <input
                  type="text"
                  name="donationFor"
                  value={formData.donationFor}
                  onChange={handleChange}
                  placeholder="e.g., New Mosque Construction in Village XYZ"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  required
                />
              </div>

              {/* Two Column Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Contact Person */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter name"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    required
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter full address"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter detailed description of the cause..."
                  rows="5"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all resize-none"
                  required
                />
              </div>

              {/* Images Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Upload Images (Max 5) *
                </label>
                <div className="space-y-3">
                  {imageInputs.map((input, index) => (
                    <div key={input.id} className="flex items-center gap-3">
                      <div className="flex-1 relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageInputChange(e, index)}
                          className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                          required={index === 0}
                        />
                      </div>
                      {imagePreviews[index] && (
                        <img
                          src={imagePreviews[index]}
                          alt={`Preview ${index + 1}`}
                          className="w-16 h-16 object-cover rounded-lg shadow-md border-2 border-gray-200"
                        />
                      )}
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeImageInput(index)}
                          className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {imageInputs.length < 5 && (
                  <button
                    type="button"
                    onClick={addImageInput}
                    className="mt-4 flex items-center gap-2 text-gray-800 font-semibold hover:text-gray-900 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add More Images
                  </button>
                )}
              </div>

              {/* Cause Type */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Cause Type *
                  </label>
                  <select
                    name="isPermanent"
                    value={formData.isPermanent}
                    onChange={handleChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  >
                    <option value="true">Permanent</option>
                    <option value="false">Temporary</option>
                  </select>
                </div>

                {/* Visible Days */}
                {formData.isPermanent === 'false' && (
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Visible for (days) *
                    </label>
                    <input
                      type="number"
                      name="visibleDays"
                      min="1"
                      value={formData.visibleDays}
                      onChange={handleChange}
                      placeholder="Enter number of days"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Adding Cause...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Cause
                  </span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddCause;


// working 16/12/25


// import React, { useState } from 'react';
// import axios from 'axios';

// const AddCause = () => {
//   const [formData, setFormData] = useState({
//     cause: '',
//     donationFor: '',
//     description: '',
//     name: '',
//     number: '',
//     address: '',
//     isPermanent: 'true',
//     visibleDays: ''  // New
// });



//     const [imageInputs, setImageInputs] = useState([{ id: 0, file: null }]);
//     const [imagePreviews, setImagePreviews] = useState([]);

//     const handleChange = (e) => {
//         setFormData({ ...formData, [e.target.name]: e.target.value });
//     };

//     const handleImageInputChange = (e, index) => {
//         const file = e.target.files[0];
//         const updatedInputs = [...imageInputs];
//         updatedInputs[index].file = file;
//         setImageInputs(updatedInputs);

//         const newPreviews = [...imagePreviews];
//         newPreviews[index] = URL.createObjectURL(file);
//         setImagePreviews(newPreviews);
//     };

//     const addImageInput = () => {
//         if (imageInputs.length >= 5) {
//             alert("Maximum 5 images allowed");
//             return;
//         }
//         setImageInputs([...imageInputs, { id: Date.now(), file: null }]);
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         const data = new FormData();

//         Object.entries(formData).forEach(([key, value]) => data.append(key, value));
//         imageInputs.forEach(({ file }) => {
//             if (file) data.append('images', file);
//         });

//         try {
//             const response = await axios.post(
//                 `${process.env.REACT_APP_REQUESTING_API}/api/causes`,
//                 data
//             );
//             alert(response.data.message);

//             // Reset form
//             setFormData({
//                 cause: '',
//                 donationFor: '',
//                 description: '',
//                 name: '',
//                 number: '',
//                 address: '',
//             });
//             setImageInputs([{ id: 0, file: null }]);
//             setImagePreviews([]);
//         } catch (error) {
//             console.error(error);
//             alert('Failed to add cause');
//         }
//     };

//     return (
//         <div>
//             <form
//                 onSubmit={handleSubmit}
//                 className="space-y-6 p-4 mt-10 border rounded-lg shadow-md bg-white max-w-md mx-auto"
//             >
//                 <h1 className="text-2xl font-bold text-center">Add a New Cause</h1>

//                 {/* Main Cause Dropdown */}
//                 <div>
//                     <label htmlFor="cause" className="block font-semibold">Main Cause</label>
//                     <select
//                         name="cause"
//                         value={formData.cause}
//                         onChange={handleChange}
//                         className="block w-full p-2 border rounded mt-1"
//                         required
//                     >
//                         <option value="">-- Select Main Cause --</option>
//                         <option value="Masjid Construction">Masjid Construction</option>
//                         <option value="Madrasa">Madrasa</option>
//                         <option value="Imaam & Mauzin Funds">Imaam & Mauzin Funds</option>
//                         <option value="Maktab & syllabaus">Maktab & syllabaus</option>
//                         <option value="Social work">Social work</option>
//                     </select>
//                 </div>

//                 {/* Other Inputs */}
//                 {['donationFor', 'name', 'number', 'address'].map((field) => (
//                     <div key={field}>
//                         <label className="block font-semibold capitalize">{field.replace(/([A-Z])/g, ' $1')}</label>
//                         <input
//                             type="text"
//                             name={field}
//                             value={formData[field]}
//                             onChange={handleChange}
//                             placeholder={`Enter ${field}`}
//                             className="block w-full p-2 border rounded mt-1"
//                         />
//                     </div>
//                 ))}

//                 {/* Description */}
//                 <div>
//                     <label htmlFor="description" className="block font-semibold">Description</label>
//                     <textarea
//                         name="description"
//                         value={formData.description}
//                         onChange={handleChange}
//                         placeholder="Enter description"
//                         className="block w-full p-2 border rounded mt-1"
//                     />
//                 </div>

//                 {/* Image Upload with Add More */}
//                 <div>
//                     <label className="block font-semibold">Upload Images (Max 5)</label>
//                     {imageInputs.map((input, index) => (
//                         <div key={input.id} className="flex items-center gap-2 mt-2">
//                             <input
//                                 type="file"
//                                 accept="image/*"
//                                 onChange={(e) => handleImageInputChange(e, index)}
//                                 className="block w-full"
//                             />
//                             {imagePreviews[index] && (
//                                 <img
//                                     src={imagePreviews[index]}
//                                     alt={`Preview ${index + 1}`}
//                                     className="w-12 h-12 object-cover border rounded"
//                                 />
//                             )}
//                         </div>
//                     ))}
//                     <button
//                         type="button"
//                         onClick={addImageInput}
//                         className="mt-2 text-blue-600 font-semibold hover:underline"
//                     >
//                         + Add More
//                     </button>
//                 </div>
// {/* Permanent or Temporary */}
// <div>
//   <label className="block font-semibold">Cause Type</label>
//   <select
//     name="isPermanent"
//     value={formData.isPermanent}
//     onChange={handleChange}
//     className="block w-full p-2 border rounded mt-1"
//   >
//     <option value="true">Permanent</option>
//     <option value="false">Temporary</option>
//   </select>
// </div>

// {/* Visible Days for Temporary Causes */}
// {formData.isPermanent === 'false' && (
//   <div>
//     <label className="block font-semibold">Visible for (days)</label>
//     <input
//       type="number"
//       name="visibleDays"
//       min="1"
//       value={formData.visibleDays}
//       onChange={handleChange}
//       placeholder="Enter number of days"
//       className="block w-full p-2 border rounded mt-1"
//       required
//     />
//   </div>
// )}

//                 {/* Submit Button */}
//                 <button
//                     type="submit"
//                     className="w-full px-4 py-2 text-white bg-zinc-600 rounded hover:bg-zinc-500"
//                 >
//                     Add Cause
//                 </button>
//             </form>
//         </div>
//     );
// };

// export default AddCause;



